/**
 * Enhances TrackingMore result markup to match track-order page layout.
 * Runs after widget injects .tracking_result_container (MutationObserver).
 * Never reparents widget-owned nodes — TrackingMore re-renders on each search.
 */
(function () {
  const ENHANCE_DEBOUNCE_MS = 120;
  let isEnhancing = false;

  function isCompleteDot(dot) {
    const bg = dot.getAttribute('style') || '';
    return bg.includes('rgb(0, 128, 0)') || bg.includes('rgb(0,128,0)');
  }

  function copyIconSvg() {
    return (
      '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>' +
      '<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>' +
      '</svg>'
    );
  }

  function checkIconSvg() {
    return (
      '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 6 9 17 4 12"></polyline>' +
      '</svg>'
    );
  }

  function ensureCardHeader(card, title) {
    if (!card) return;
    let header = card.querySelector(':scope > .semi-card-header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'semi-card-header track-side-card-header';
      card.insertBefore(header, card.firstChild);
    }
    header.innerHTML = '<h3 class="track-side-card-title">' + title + '</h3>';
  }

  function cleanupEnhancements(container) {
    container.querySelector('.track-status-banner')?.remove();
    container.querySelector('.track-package-card--display')?.remove();
    container.querySelectorAll('.track-progress-steps--mirror').forEach(function (el) {
      el.remove();
    });
    delete container.dataset.trackEnhanceKey;
    delete container.dataset.trackProgressMirrorKey;

    container.querySelectorAll('.track_map_detail_item--hidden-source').forEach(function (item) {
      item.classList.remove('track_map_detail_item--hidden-source');
    });
  }

  function getEnhanceKey(container) {
    const orderEl = container.querySelector('.track_result_order');
    const trackingTextEl = container.querySelector('.track_map_detail_item_text');
    const dotCount = container.querySelectorAll('.track_progress_dot').length;

    return [
      orderEl ? orderEl.textContent.trim() : '',
      trackingTextEl ? trackingTextEl.textContent.trim() : '',
      String(dotCount),
    ].join('\0');
  }

  function isIgnorableMutationNode(node) {
    if (!node || node.nodeType !== 1) return true;

    return Boolean(
      node.closest(
        '.track-order-recommend, .track-progress-steps--mirror, .track-package-card--display, .track-side-card-header'
      ) ||
        node.classList.contains('track-order-recommend') ||
        node.classList.contains('track-progress-steps--mirror') ||
        node.classList.contains('track-progress-step') ||
        node.classList.contains('track-package-card--display') ||
        node.classList.contains('track-side-card-header') ||
        node.classList.contains('track-event-badge') ||
        node.classList.contains('track-order-row__meta')
    );
  }

  function shouldIgnoreMutation(mutation) {
    const target = mutation.target;
    if (target instanceof Element) {
      if (
        target.closest(
          '.track-order-recommend, .track-progress-steps--mirror, .track-package-card--display'
        )
      ) {
        return true;
      }
    }

    if (mutation.type !== 'childList') return false;

    const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
    if (!nodes.length) return false;

    return nodes.every(isIgnorableMutationNode);
  }

  function removeRecommendSection() {
    document.querySelectorAll('.track-order-recommend').forEach(function (section) {
      section.remove();
    });
  }

  function formatRecommendMoney(cents) {
    const moneyFormat = window.__TRACK_ORDER_MONEY_FORMAT__ || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/g;
    const value = Number(cents);

    if (!Number.isFinite(value)) return '';

    const formatWithDelimiters = function (number, precision, thousands, decimal) {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };

    return moneyFormat.replace(placeholderRegex, function (_match, token) {
      if (token === 'amount_no_decimals' || token === 'amount_no_decimals_with_comma_separator') {
        return formatWithDelimiters(value, 0, ',', '.');
      }
      return formatWithDelimiters(value, 2, ',', '.');
    });
  }

  function setMoneyContent(el, centsOrHtml) {
    const raw = String(centsOrHtml ?? '');

    if (/<[a-z][\s\S]*>/i.test(raw)) {
      el.innerHTML = raw;
      return;
    }

    const formatted = formatRecommendMoney(Number(raw));
    if (/<[a-z]/i.test(formatted)) {
      el.innerHTML = formatted;
    } else {
      el.textContent = formatted;
    }
  }

  function normalizeImageUrl(src) {
    if (!src) return '';
    if (String(src).indexOf('//') === 0) return 'https:' + src;
    return String(src);
  }

  function formatRecommendTitle(title) {
    if (window.formatProductTitle) return window.formatProductTitle(title);
    return title;
  }

  function getRecommendProducts(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && Array.isArray(payload.data.products)) return payload.data.products;
    return [];
  }

  function getRecommendMount(resultsContainer) {
    if (resultsContainer && resultsContainer.parentElement) {
      return resultsContainer.parentElement;
    }

    return (
      document.querySelector('#TrackingMore-tracking-widget') ||
      document.querySelector('[id*="tracking_app"] #TrackingMore-tracking-widget')
    );
  }

  function renderRecommendCard(product) {
    const card = document.createElement('div');
    card.className = 'related-product-card';

    const link = document.createElement('a');
    link.className = 'related-content';
    link.href = product.url || '/products/' + (product.handle_title || '');

    const imageWrap = document.createElement('div');
    imageWrap.className = 'related-image-wrap';

    const image = document.createElement('img');
    image.className = 'related-image';
    image.loading = 'lazy';
    image.src = normalizeImageUrl(product.img);
    image.alt = product.title || '';
    imageWrap.appendChild(image);

    const attributes = document.createElement('div');
    attributes.className = 'related-attributes';

    const title = document.createElement('div');
    title.className = 'related-title';
    title.textContent = formatRecommendTitle(product.title || '');

    const type = document.createElement('div');
    type.className = 'related-type';
    type.textContent = product.product_type || '';

    const priceWrap = document.createElement('div');
    priceWrap.className = 'related-price-wrap';

    const compareCents = Number(product.compare_price);
    if (Number.isFinite(compareCents) && compareCents > Number(product.price)) {
      const comparePrice = document.createElement('div');
      comparePrice.className = 'related-compare-price';
      setMoneyContent(comparePrice, compareCents);
      priceWrap.appendChild(comparePrice);
    }

    const price = document.createElement('div');
    price.className = 'related-price';
    setMoneyContent(price, product.price);
    priceWrap.appendChild(price);

    attributes.append(title, type, priceWrap);
    link.append(imageWrap, attributes);
    card.appendChild(link);

    return card;
  }

  function renderRecommendSection(products, resultsContainer) {
    const items = products.filter(function (product) {
      return product && (product.url || product.handle_title) && product.img;
    });

    if (!items.length) return;

    const mount = getRecommendMount(resultsContainer);
    if (!mount) return;

    const existing = mount.querySelector(':scope > .track-order-recommend');
    if (existing && existing.dataset.recommendKey === items.map(function (p) { return p.url; }).join('|')) {
      return;
    }

    removeRecommendSection();

    const section = document.createElement('section');
    section.className = 'track-order-recommend related-products-section';
    section.setAttribute('aria-label', 'Recommended products');
    section.dataset.recommendKey = items.map(function (p) { return p.url; }).join('|');

    const wrapper = document.createElement('div');
    wrapper.className = 'related-products-wrapper';

    const heading = document.createElement('h2');
    heading.className = 'related-recommendations-heading';
    heading.textContent = 'You may also like...';

    const grid = document.createElement('div');
    grid.className = 'related-products-grid desktop-cols-4 mobile-cols-2';

    items.forEach(function (product) {
      grid.appendChild(renderRecommendCard(product));
    });

    wrapper.append(heading, grid);
    section.appendChild(wrapper);

    if (resultsContainer && resultsContainer.parentElement) {
      resultsContainer.insertAdjacentElement('afterend', section);
    } else {
      mount.appendChild(section);
    }
  }

  function handleRecommendEvent(event) {
    const products = getRecommendProducts(event.detail);
    if (!products.length) return;

    const widget =
      document.querySelector('#TrackingMore-tracking-widget') ||
      document.querySelector('[id*="tracking_app"] #TrackingMore-tracking-widget');
    const results = widget ? widget.querySelector('.tracking_result_container') : null;

    if (!results) return;

    renderRecommendSection(products, results);
  }

  function enhanceOrderRow(orderEl, trackingTextEl) {
    if (!orderEl) return;

    orderEl.classList.add('track-order-row');

    const existingId = orderEl.querySelector('.track-order-row__id');
    if (existingId) {
      if (trackingTextEl) {
        const metaValue = orderEl.querySelector('.track-order-row__meta-value');
        if (metaValue) {
          metaValue.textContent = trackingTextEl.textContent.trim();
        }
      }
      return;
    }

    const orderText = orderEl.textContent.trim();
    orderEl.textContent = '';

    const orderId = document.createElement('span');
    orderId.className = 'track-order-row__id';
    orderId.textContent = orderText;
    orderEl.appendChild(orderId);

    if (!trackingTextEl) return;

    const trackingNo = trackingTextEl.textContent.trim();
    const meta = document.createElement('div');
    meta.className = 'track-order-row__meta';
    meta.innerHTML =
      '<span class="track-order-row__meta-label">Tracking Number:</span>' +
      '<span class="track-order-row__meta-value"></span>' +
      '<button type="button" class="track-order-row__copy" aria-label="Copy tracking number">' +
      copyIconSvg() +
      '</button>';
    meta.querySelector('.track-order-row__meta-value').textContent = trackingNo;
    meta.querySelector('.track-order-row__copy').addEventListener('click', function () {
      const btn = this;
      const originalHtml = btn.innerHTML;
      const originalLabel = btn.getAttribute('aria-label');

      navigator.clipboard.writeText(trackingNo).then(function () {
        btn.innerHTML = checkIconSvg();
        btn.classList.add('track-order-row__copy--copied');
        btn.setAttribute('aria-label', 'Copied');

        window.setTimeout(function () {
          btn.innerHTML = originalHtml;
          btn.classList.remove('track-order-row__copy--copied');
          btn.setAttribute('aria-label', originalLabel);
        }, 2000);
      }).catch(function () {});
    });
    orderEl.appendChild(meta);
  }

  function enhancePackageCard(detailsCard) {
    if (!detailsCard || detailsCard.parentElement?.querySelector('.track-package-card--display')) return;

    const items = [...detailsCard.querySelectorAll('.track_map_detail_item')];
    const productItem = items.find(function (item) {
      const title = item.querySelector('.track_map_detail_item_title');
      return title && title.textContent.trim() === 'Product';
    });

    if (!productItem) return;

    const packageCard = document.createElement('div');
    packageCard.className = 'semi-card semi-card-bordered track-package-card track-package-card--display';
    packageCard.setAttribute('aria-hidden', 'true');

    const packageHeader = document.createElement('div');
    packageHeader.className = 'semi-card-header track-side-card-header';
    packageHeader.innerHTML = '<h3 class="track-side-card-title">Package Information</h3>';

    const packageBody = document.createElement('div');
    packageBody.className = 'semi-card-body';
    packageBody.appendChild(productItem.cloneNode(true));

    packageCard.append(packageHeader, packageBody);
    detailsCard.after(packageCard);
    productItem.classList.add('track_map_detail_item--hidden-source');
  }

  function isPendingResultContainer(container) {
    if (!container) return false;
    if (container.querySelector('.track_progress')) return false;

    const statusText = container.textContent || '';
    return /pending|not found|could not find/i.test(statusText);
  }

  function enhanceResults(container) {
    if (!container) return;

    if (isPendingResultContainer(container)) {
      cleanupEnhancements(container);
      return;
    }

    try {
      enhanceResultsInner(container);
    } catch (error) {
      cleanupEnhancements(container);
    }
  }

  function enhanceResultsInner(container) {
    const enhanceKey = getEnhanceKey(container);
    const isSameResult = container.dataset.trackEnhanceKey === enhanceKey;

    if (isSameResult) {
      const progressEl = container.querySelector('.track_progress');
      if (progressEl) {
        syncProgressLine(container);
        buildProgressStepMirror(progressEl);
      }
      return;
    }

    cleanupEnhancements(container);
    container.dataset.trackEnhanceKey = enhanceKey;

    const orderEl = container.querySelector('.track_result_order');
    const progressEl = container.querySelector('.track_progress');
    const trackingTextEl = container.querySelector('.track_map_detail_item_text');

    if (orderEl && progressEl) {
      orderEl.classList.add('track-status-card__order');
      progressEl.classList.add('track-status-card__progress');
      enhanceOrderRow(orderEl, trackingTextEl);
    }

    const mapBlock = container.querySelector('.track_map_info_block');

    const timelineCard = mapBlock ? mapBlock.querySelector(':scope > .semi-card') : null;
    if (timelineCard) {
      ensureCardHeader(timelineCard, 'Shipment Activity');
    }

    const sidebar = container.querySelector('.track_info_right');
    const sidebarCards = sidebar
      ? [...sidebar.querySelectorAll(':scope > .semi-card')].filter(function (card) {
          return !card.querySelector('.semi-spin-children:empty');
        })
      : [];

    const detailsCard = sidebarCards.find(function (card) {
      return card.querySelector('.track_map_detail_item');
    });

    if (detailsCard) {
      ensureCardHeader(detailsCard, 'Shipment Details');
      enhancePackageCard(detailsCard);
    }

    if (container.querySelector('.track_progress')) {
      enhanceProgressSteps(container);
    }

    const firstTimelineItem = container.querySelector('.semi-timeline-item');
    if (firstTimelineItem) {
      firstTimelineItem.classList.add('is-current');
      const mock = firstTimelineItem.querySelector('.map_info_mock');
      if (mock && !mock.querySelector('.track-event-badge')) {
        const badge = document.createElement('span');
        badge.className = 'track-event-badge';
        badge.textContent = 'Current Status';
        mock.insertBefore(badge, mock.firstChild);
      }
    }
  }

  function parseProgressStepDate(text) {
    const raw = String(text || '').trim();
    if (!raw || raw === '—') return NaN;
    const ts = Date.parse(raw);
    return Number.isNaN(ts) ? NaN : ts;
  }

  function getTimelineEvents(container) {
    return [...container.querySelectorAll('.track_map_info_block .semi-timeline-item')]
      .map(function (item) {
        const mockItems = item.querySelectorAll('.map_info_mock_item');
        const timeText = mockItems[0]?.textContent?.trim() || '';
        const bodyText = (mockItems[mockItems.length - 1]?.textContent?.trim() || '').toLowerCase();
        const ts = parseProgressStepDate(timeText);

        return { ts, timeText, bodyText };
      })
      .filter(function (event) {
        return !Number.isNaN(event.ts);
      });
  }

  function findEarliestTimelineMatch(events, patterns) {
    const matches = events.filter(function (event) {
      return patterns.some(function (pattern) {
        return pattern.test(event.bodyText);
      });
    });

    if (!matches.length) return null;

    return matches.reduce(function (earliest, event) {
      return event.ts < earliest.ts ? event : earliest;
    });
  }

  function findLatestTimelineMatch(events, patterns) {
    const matches = events.filter(function (event) {
      return patterns.some(function (pattern) {
        return pattern.test(event.bodyText);
      });
    });

    if (!matches.length) return null;

    return matches.reduce(function (latest, event) {
      return event.ts > latest.ts ? event : latest;
    });
  }

  const TRANSIT_EVENT_PATTERNS = [
    /picked up/,
    /in transit/,
    /departed/,
    /arrived at/,
    /flight/,
    /airport/,
    /customs/,
    /clearance/,
    /facility/,
    /sort facility/,
  ];

  function stepMatchesTimelineEvent(title, bodyText) {
    const step = title.toLowerCase();
    const event = bodyText.toLowerCase();

    if (step.includes('ready')) {
      return /information received|order ready|ready to ship/.test(event);
    }

    if (step.includes('transit')) {
      return (
        !/delivered|out for delivery/.test(event) &&
        TRANSIT_EVENT_PATTERNS.some(function (pattern) {
          return pattern.test(event);
        })
      );
    }

    if (step.includes('out for')) {
      return /out for delivery/.test(event);
    }

    if (step.includes('deliver')) {
      return /delivered/.test(event);
    }

    return false;
  }

  function correctProgressStepDates(container, textBoxes, lastCompleteIndex) {
    const events = getTimelineEvents(container);
    if (!events.length) return;

    const currentEvent = events[0] || null;

    textBoxes.forEach(function (box, index) {
      const titleEl = box.querySelector('.track_progress_title');
      const dateEl = box.querySelector('.track_progress_date');
      if (!titleEl || !dateEl) return;

      const title = titleEl.textContent.trim().toLowerCase();
      let match = null;

      if (
        index === lastCompleteIndex &&
        currentEvent &&
        stepMatchesTimelineEvent(title, currentEvent.bodyText)
      ) {
        dateEl.textContent = currentEvent.timeText;
        dateEl.dataset.trackDateCorrected = 'current';
        return;
      }

      if (title.includes('ready')) {
        match = findEarliestTimelineMatch(events, [/information received/, /order ready/, /ready to ship/]);
      } else if (title.includes('transit') || /shipped|shipping/.test(title)) {
        match = findLatestTimelineMatch(events, TRANSIT_EVENT_PATTERNS);
      } else if (title.includes('out for')) {
        match = findLatestTimelineMatch(events, [/out for delivery/]);
      } else if (title.includes('deliver')) {
        match = findLatestTimelineMatch(events, [/delivered/]);
      }

      if (match?.timeText) {
        dateEl.textContent = match.timeText;
        dateEl.dataset.trackDateCorrected = 'true';
      }
    });
  }

  function getProgressStepPairs(iconRow, textRow) {
    const wraps = [...iconRow.querySelectorAll(':scope > .track-step-icon')];
    const textBoxes = [...textRow.querySelectorAll(':scope > .track_progress_text_box')];

    if (!wraps.length || wraps.length !== textBoxes.length) return [];

    return wraps.map(function (wrap, index) {
      return {
        wrap,
        textBox: textBoxes[index],
        index,
      };
    });
  }

  function wrapProgressIcons(iconRow) {
    if (!iconRow) return;

    iconRow.querySelectorAll(':scope > svg').forEach(function (svg) {
      if (svg.parentElement?.classList.contains('track-step-icon')) return;

      const wrap = document.createElement('span');
      wrap.className = 'track-step-icon';
      iconRow.insertBefore(wrap, svg);
      wrap.appendChild(svg);
    });
  }

  function enhanceProgressSteps(container) {
    const progressBlock = container.querySelector('.track_progress');
    const iconRow = progressBlock
      ? progressBlock.querySelector('.track_progress_icon')
      : container.querySelector('.track_progress_icon');

    if (iconRow) {
      iconRow.hidden = false;
    }

    const textRow = progressBlock?.querySelector('.track_progress_text');
    if (textRow) {
      textRow.hidden = false;
    }

    wrapProgressIcons(iconRow);

    const textBoxes = textRow
      ? [...textRow.querySelectorAll(':scope > .track_progress_text_box')]
      : [...container.querySelectorAll('.track_progress_text_box')];

    if (iconRow) {
      iconRow.querySelectorAll(':scope > .track-step-icon').forEach(function (wrap) {
        wrap.style.order = '';
      });
    }

    if (textRow) {
      textRow.querySelectorAll(':scope > .track_progress_text_box').forEach(function (box) {
        box.style.order = '';
      });
    }

    const dots = progressBlock
      ? [...progressBlock.querySelectorAll('.track_progress_dot')]
      : [...container.querySelectorAll('.track_progress_dot')];

    let lastComplete = -1;
    dots.forEach(function (dot, index) {
      if (isCompleteDot(dot)) lastComplete = index;
    });

    correctProgressStepDates(container, textBoxes, lastComplete);

    const wraps = iconRow ? [...iconRow.querySelectorAll(':scope > .track-step-icon')] : [];

    dots.forEach(function (dot, index) {
      const wrap = wraps[index];
      const textBox = textBoxes[index];
      if (!wrap) return;

      wrap.classList.remove('is-complete', 'is-pending', 'is-current');
      textBox?.classList.remove('is-complete', 'is-pending', 'is-current');

      if (isCompleteDot(dot)) {
        wrap.classList.add('is-complete');
        textBox?.classList.add('is-complete');
        if (index === lastComplete) {
          wrap.classList.add('is-current');
          textBox?.classList.add('is-current');
        }
      } else {
        wrap.classList.add('is-pending');
        textBox?.classList.add('is-pending');
        const dateEl = textBox?.querySelector('.track_progress_date');
        if (dateEl && !dateEl.textContent.trim()) {
          dateEl.textContent = '—';
        }
      }
    });

    syncProgressLine(container);

    if (progressBlock) {
      buildProgressStepMirror(progressBlock);
    }
  }

  function getProgressMirrorKey(progressEl) {
    const iconRow = progressEl.querySelector('.track_progress_icon');
    const textRow = progressEl.querySelector('.track_progress_text');
    const colorLine = progressEl.querySelector('.track_progress_colorLine');

    if (!iconRow || !textRow) return '';

    const wraps = [...iconRow.querySelectorAll('.track-step-icon')];
    const textState = [...textRow.querySelectorAll('.track_progress_text_box')].map(function (box) {
      return box.className + ':' + box.textContent.trim();
    });
    const dotState = [...progressEl.querySelectorAll('.track_progress_dot')].map(function (dot) {
      return dot.getAttribute('style') || '';
    });

    return [
      String(wraps.length),
      textState.join('|'),
      dotState.join('|'),
      colorLine ? colorLine.style.width : '',
    ].join('\0');
  }

  function buildProgressStepMirror(progressEl) {
    const mirrorKey = getProgressMirrorKey(progressEl);
    const existing = progressEl.querySelector('.track-progress-steps--mirror');

    if (existing && progressEl.dataset.trackProgressMirrorKey === mirrorKey) {
      return;
    }

    existing?.remove();
    progressEl.dataset.trackProgressMirrorKey = mirrorKey;

    const iconRow = progressEl.querySelector('.track_progress_icon');
    const textRow = progressEl.querySelector('.track_progress_text');
    if (!iconRow || !textRow) return;

    const pairs = getProgressStepPairs(iconRow, textRow);
    if (!pairs.length) return;

    const stepsRoot = document.createElement('div');
    stepsRoot.className = 'track-progress-steps track-progress-steps--mirror';
    stepsRoot.setAttribute('aria-hidden', 'true');
    stepsRoot.style.setProperty('--track-step-count', String(pairs.length));

    pairs.forEach(function (pair) {
      const step = document.createElement('div');
      step.className = 'track-progress-step';
      step.appendChild(pair.wrap.cloneNode(true));
      step.appendChild(pair.textBox.cloneNode(true));
      stepsRoot.appendChild(step);
    });

    progressEl.appendChild(stepsRoot);
  }

  function syncProgressLine(container) {
    const colorLine = container.querySelector('.track_progress_colorLine');
    const iconRow = container.querySelector('.track_progress_icon');
    if (!colorLine || !iconRow) return;

    const width = colorLine.style.width;
    if (!width) return;

    const pct = parseFloat(width) / 100;
    if (!Number.isNaN(pct)) {
      iconRow.style.setProperty('--track-line-ratio', String(pct));
    }
  }

  function observeWidget() {
    const widget =
      document.querySelector('#TrackingMore-tracking-widget') ||
      document.querySelector('[id*="tracking_app"] #TrackingMore-tracking-widget');

    if (!widget) return;

    let enhanceTimer;

    const tryEnhance = function () {
      window.clearTimeout(enhanceTimer);
      enhanceTimer = window.setTimeout(function () {
        const results = widget.querySelector('.tracking_result_container');
        isEnhancing = true;
        try {
          if (results) {
            enhanceResults(results);
          } else {
            removeRecommendSection();
          }
        } catch (error) {
          removeRecommendSection();
        } finally {
          window.setTimeout(function () {
            isEnhancing = false;
          }, 0);
        }
      }, ENHANCE_DEBOUNCE_MS);
    };

    tryEnhance();

    const observer = new MutationObserver(function (mutations) {
      if (isEnhancing) return;
      if (mutations.every(shouldIgnoreMutation)) return;
      tryEnhance();
    });
    observer.observe(widget, { childList: true, subtree: true });
  }

  window.addEventListener('track-order:recommend', handleRecommendEvent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeWidget);
  } else {
    observeWidget();
  }
})();
